import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { ShieldOff } from "lucide-react";
import type { ReactNode } from "react";

import { useAuth } from "./auth";
import type { FeatureName } from "./lib/features";
import AppShell, { firstAccessibleRoute } from "./components/AppShell";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import CustomerLedger from "./pages/CustomerLedger";
import Products from "./pages/Products";
import InventoryAttributes from "./pages/InventoryAttributes";
import Quotations from "./pages/Quotations";
import QuotationForm from "./pages/QuotationForm";
import Receipts from "./pages/Receipts";
import ReceiptForm from "./pages/ReceiptForm";
import Waybills from "./pages/Waybills";
import WaybillForm from "./pages/WaybillForm";
import SalesInvoices from "./pages/SalesInvoices";
import SalesInvoiceForm from "./pages/SalesInvoiceForm";
import Finance from "./pages/Finance";
import FinanceCustomerLedger from "./pages/FinanceCustomerLedger";
import GeneralLedgerShell from "./pages/gl/GeneralLedgerShell";
import GeneralLedgerOverview from "./pages/gl/GeneralLedgerOverview";
import GeneralLedgerAccounts from "./pages/gl/GeneralLedgerAccounts";
import GeneralLedgerJournals from "./pages/gl/GeneralLedgerJournals";
import GeneralLedgerJournalForm from "./pages/gl/GeneralLedgerJournalForm";
import GeneralLedgerJournalDetail from "./pages/gl/GeneralLedgerJournalDetail";
import GeneralLedgerTrialBalance from "./pages/gl/GeneralLedgerTrialBalance";
import GeneralLedgerLedger from "./pages/gl/GeneralLedgerLedger";
import GeneralLedgerStatements from "./pages/gl/GeneralLedgerStatements";
import GeneralLedgerPeriods from "./pages/gl/GeneralLedgerPeriods";
import GeneralLedgerAudit from "./pages/gl/GeneralLedgerAudit";
import Settings from "./pages/Settings";
import ProductionOrders from "./pages/ProductionOrders";
import ProductionOrderForm from "./pages/ProductionOrderForm";

function SalesInvoiceEditRedirect() {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  return <Navigate to={`/sales-invoices/${id}${search}`} replace />;
}

function InventoryAttributesRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const tab = params.get("tab");
  if (tab === "gauges" || tab === "profiles" || tab === "colors") {
    return <InventoryAttributes />;
  }
  return <Navigate to="/inventory/attributes?tab=gauges" replace />;
}

function Private({ children }: { children: ReactNode }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

// Where to send a user when a route is denied. We never hard-redirect to "/"
// anymore: "/" is the Dashboard and is itself module-gated, so a
// dashboard-denied user bounced to "/" would loop. Instead we resolve their
// first reachable module (in sidebar order) and fall back to the ungated
// /no-access page when they can reach nothing.
function deniedRedirect(
  canAccess: (m: string) => boolean,
  isFeatureEnabled: (f: FeatureName) => boolean,
): string {
  return firstAccessibleRoute(canAccess, isFeatureEnabled) ?? "/no-access";
}

// Gate a route behind a product-level feature flag. When the flag is OFF for
// this deployment the module is invisible, so we redirect to the user's first
// accessible landing route (matching RequireAccess and the unknown-route
// fallback) rather than render anything.
function RequireFeature({
  feature,
  children,
}: {
  feature: FeatureName;
  children: ReactNode;
}) {
  const { isFeatureEnabled, canAccess } = useAuth();
  if (!isFeatureEnabled(feature)) {
    return <Navigate to={deniedRedirect(canAccess, isFeatureEnabled)} replace />;
  }
  return <>{children}</>;
}

// Client-side mirror of the server's per-action permission checks. The API is
// the authoritative gate, but guarding the route here is defense-in-depth and
// avoids rendering a broken/empty page (or firing 403s) for a user who lacks
// the module's `view` permission. `module` is a key from lib/permissions
// MODULES — exactly what the sidebar uses via canAccess() — so a guarded route
// is reachable iff its nav entry would be visible. Lacking access redirects to
// the user's first accessible landing route (never a hard "/", which would
// loop for a dashboard-denied user), or /no-access when nothing is reachable.
function RequireAccess({
  module,
  children,
}: {
  module: string;
  children: ReactNode;
}) {
  const { canAccess, isFeatureEnabled } = useAuth();
  if (!canAccess(module)) {
    return <Navigate to={deniedRedirect(canAccess, isFeatureEnabled)} replace />;
  }
  return <>{children}</>;
}

// Friendly terminal state for a signed-in user who has been granted no module
// access at all. Rendered inside <Private> (so it requires a session and keeps
// the app chrome) but deliberately NOT wrapped in RequireAccess, so it can
// never redirect and therefore never loops.
function NoAccess() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
        <ShieldOff className="h-7 w-7" />
      </div>
      <h1 className="mt-5 text-xl font-semibold text-foreground">
        No modules available
      </h1>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        You don't have access to any modules yet. Ask your administrator to
        grant you access.
      </p>
    </div>
  );
}

// Catch-all / index landing resolver. Sends the user to their first accessible
// route, /no-access when they can reach nothing, /login when signed out.
function LandingRedirect() {
  const { user, ready, canAccess, isFeatureEnabled } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={deniedRedirect(canAccess, isFeatureEnabled)} replace />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <Private>
            <RequireAccess module="dashboard">
              <Dashboard />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/no-access"
        element={
          <Private>
            <NoAccess />
          </Private>
        }
      />
      <Route
        path="/customers"
        element={
          <Private>
            <RequireAccess module="customers">
              <Customers />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/customers/:id/ledger"
        element={
          <Private>
            <RequireAccess module="customer_ledger">
              <CustomerLedger />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/products"
        element={
          <Private>
            <RequireAccess module="products">
              <Products />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/inventory/attributes"
        element={
          <Private>
            <RequireAccess module="inventory_attributes">
              <InventoryAttributesRedirect />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/quotations"
        element={
          <Private>
            <RequireAccess module="quotations">
              <Quotations />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/quotations/new"
        element={
          <Private>
            <RequireAccess module="quotations">
              <QuotationForm />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/quotations/:id"
        element={
          <Private>
            <RequireAccess module="quotations">
              <QuotationForm />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/quotations/:id/edit"
        element={
          <Private>
            <RequireAccess module="quotations">
              <QuotationForm />
            </RequireAccess>
          </Private>
        }
      />

      <Route
        path="/receipts"
        element={
          <Private>
            <RequireAccess module="receipts">
              <Receipts />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/receipts/new"
        element={
          <Private>
            <RequireAccess module="receipts">
              <ReceiptForm />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/receipts/cancelled/:id"
        element={
          <Private>
            <RequireAccess module="receipts">
              <ReceiptForm />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/receipts/:id"
        element={
          <Private>
            <RequireAccess module="receipts">
              <ReceiptForm />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/receipts/:id/edit"
        element={
          <Private>
            <RequireAccess module="receipts">
              <ReceiptForm />
            </RequireAccess>
          </Private>
        }
      />

      <Route
        path="/waybills"
        element={
          <Private>
            <RequireAccess module="waybills">
              <Waybills />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/waybills/new"
        element={
          <Private>
            <RequireAccess module="waybills">
              <WaybillForm />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/waybills/cancelled/:id"
        element={
          <Private>
            <RequireAccess module="waybills">
              <WaybillForm />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/waybills/:id"
        element={
          <Private>
            <RequireAccess module="waybills">
              <WaybillForm />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/waybills/:id/edit"
        element={
          <Private>
            <RequireAccess module="waybills">
              <WaybillForm />
            </RequireAccess>
          </Private>
        }
      />

      <Route
        path="/sales-invoices"
        element={
          <Private>
            <RequireAccess module="sales_invoices">
              <SalesInvoices />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/sales-invoices/new"
        element={
          <Private>
            <RequireAccess module="sales_invoices">
              <SalesInvoiceForm />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/sales-invoices/cancelled/:id"
        element={
          <Private>
            <RequireAccess module="sales_invoices">
              <SalesInvoiceForm />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/sales-invoices/:id"
        element={
          <Private>
            <RequireAccess module="sales_invoices">
              <SalesInvoiceForm />
            </RequireAccess>
          </Private>
        }
      />
      <Route
        path="/sales-invoices/:id/edit"
        element={
          <Private>
            <RequireAccess module="sales_invoices">
              <SalesInvoiceEditRedirect />
            </RequireAccess>
          </Private>
        }
      />

      <Route
        path="/production-orders"
        element={
          <Private>
            <RequireFeature feature="production_orders">
              <RequireAccess module="production_orders">
                <ProductionOrders />
              </RequireAccess>
            </RequireFeature>
          </Private>
        }
      />
      <Route
        path="/production-orders/new"
        element={
          <Private>
            <RequireFeature feature="production_orders">
              <RequireAccess module="production_orders">
                <ProductionOrderForm />
              </RequireAccess>
            </RequireFeature>
          </Private>
        }
      />
      <Route
        path="/production-orders/:id"
        element={
          <Private>
            <RequireFeature feature="production_orders">
              <RequireAccess module="production_orders">
                <ProductionOrderForm />
              </RequireAccess>
            </RequireFeature>
          </Private>
        }
      />

      <Route
        path="/finance/customer-ledger"
        element={
          <Private>
            <RequireAccess module="customer_ledger">
              <FinanceCustomerLedger />
            </RequireAccess>
          </Private>
        }
      />

      <Route
        path="/finance"
        element={
          <Private>
            <RequireAccess module="finance">
              <Finance />
            </RequireAccess>
          </Private>
        }
      />

      <Route
        path="/general-ledger"
        element={
          <Private>
            <RequireAccess module="general_ledger">
              <GeneralLedgerShell />
            </RequireAccess>
          </Private>
        }
      >
        <Route index element={<GeneralLedgerOverview />} />
        <Route path="accounts" element={<GeneralLedgerAccounts />} />
        <Route path="journals" element={<GeneralLedgerJournals />} />
        <Route path="journals/new" element={<GeneralLedgerJournalForm />} />
        <Route path="journals/:id" element={<GeneralLedgerJournalDetail />} />
        <Route path="journals/:id/edit" element={<GeneralLedgerJournalForm />} />
        <Route
          path="reports/trial-balance"
          element={<GeneralLedgerTrialBalance />}
        />
        <Route path="reports/ledger" element={<GeneralLedgerLedger />} />
        <Route
          path="reports/statements"
          element={<GeneralLedgerStatements />}
        />
        <Route path="periods" element={<GeneralLedgerPeriods />} />
        <Route path="audit" element={<GeneralLedgerAudit />} />
      </Route>

      <Route
        path="/settings"
        element={
          <Private>
            <Settings />
          </Private>
        }
      />
      <Route path="*" element={<LandingRedirect />} />
    </Routes>
  );
}
